import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../../../../features/admin/auth/services/auth.service';
import { MatConfirmDialogComponent } from '../../../../shared/components/modals/mat-confirm-dialog/mat-confirm-dialog/mat-confirm-dialog.component';
import { NotificationService } from '../../../../core/services/notification.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private sessionCheckInterval: any;
  private dialogRef?: MatDialogRef<MatConfirmDialogComponent>;
  private dialogShown = false;
  private warningThreshold = 60 * 1000; // 1 min antes del vencimiento
  private checkEvery = 5000; // chequeo cada 5s

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
  ) { }

  // ======================================================
  // 🧠 Inicia el timer de sesión
  // ======================================================
  startSessionTimer(): void {
    this.clearTimer();
    const token = this.authService.getToken();
    if (!token) return;

    let decodedToken: any;
    try {
      decodedToken = jwtDecode(token);
    } catch (error) {
      console.error('❌ Error decodificando token:', error);
      this.authService.logout();
      return;
    }

    const expTime = decodedToken.exp * 1000;
    this.dialogShown = false;

    this.sessionCheckInterval = setInterval(() => {
      const now = Date.now();
      const remaining = expTime - now;

      if (remaining <= 0) {
        console.warn('⚠️ Token expirado, cerrando sesión...');
        this.forceLogout();
      }
      else if (remaining <= this.warningThreshold && !this.dialogShown) {
        this.dialogShown = true;
        this.showSessionExpireDialog(Math.round(remaining / 1000));
      }
    }, this.checkEvery);
  }

  // ======================================================
  // 🔔 Muestra el diálogo de advertencia
  // ======================================================
  private showSessionExpireDialog(secondsLeft: number): void {
    if (this.dialogRef) return; // ya mostrado

    this.dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      data: {
        icon: 'alarm',
        title: '⚠️ Sesión por expirar',
        message: `Tu sesión expirará en ${secondsLeft} segundos. ¿Deseas extenderla?`,
        confirmText: 'Sí, continuar',
        cancelText: 'No, cerrar',
        timeout: secondsLeft
      },
      disableClose: true,
      width: '400px',
      panelClass: 'custom-dialog-container',
      autoFocus: false,
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = undefined;
      this.dialogShown = false;

      if (result) {
        this.extendSession();
      } else {
        this.forceLogout();
      }
    });
  }

  // ======================================================
  // ♻️ Extiende la sesión pidiendo nuevo token
  // ======================================================
  private extendSession(): void {
    this.authService.refreshToken().subscribe({
      next: (response: any) => {
        const newToken = response?.token || response;
        if (newToken) {
          this.authService.setToken(newToken);
          this.notificationService.showSuccess('Sesión extendida correctamente');
          this.startSessionTimer(); // reinicia con nuevo token
        } else {
          this.notificationService.showError('Error al extender sesión');
          this.forceLogout();
        }
      },
      error: (err) => {
        console.error('❌ Error al extender sesión:', err);
        this.notificationService.showError('No se pudo extender la sesión');
        this.forceLogout();
      }
    });
  }

  // ======================================================
  // 🔥 Cierra sesión completamente
  // ======================================================
  private forceLogout(): void {
    this.clearTimer();
    this.closeAllDialogs();
    this.authService.logout();
    this.notificationService.showError('Sesión cerrada por inactividad o expiración');
  }

  // ======================================================
  // 🧹 Limpia intervalos y flags
  // ======================================================
  clearTimer(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    this.dialogShown = false;
  }

  // ======================================================
  // 🧱 Cierra cualquier modal activo
  // ======================================================
  private closeAllDialogs(): void {
    try {
      const containers = document.querySelectorAll('.cdk-overlay-container');
      containers.forEach((c: any) => c.innerHTML = '');
    } catch (err) {
      console.error('Error al limpiar modales:', err);
    }
  }
}
